import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@repo/ui";
import { AlertCircle, Loader2, Lock } from "lucide-react";
import { useState } from "react";

interface LastNameModalProps {
  isOpen: boolean;
  onSubmit: (lastName: string) => void;
  error?: string;
  isLoading?: boolean;
  blockedUntil?: number;
  evaluationType?: string;
  isAnonymous?: boolean;
}

// Mapping des types d'évaluation vers leurs noms d'affichage
const EVALUATION_TYPE_LABELS: Record<string, string> = {
  inbasket: "In-Basket",
  form: "Formulaire",
  "fairview": "FairView",
};

function formatRemainingTime(blockedUntil: number): string {
  const now = Date.now();
  const remaining = Math.max(0, blockedUntil - now);
  const minutes = Math.ceil(remaining / 60000);
  if (minutes <= 1) return "moins d'une minute";
  return `${minutes} minutes`;
}

export function LastNameModal({
  isOpen,
  onSubmit,
  error,
  isLoading,
  blockedUntil,
  evaluationType,
  isAnonymous,
}: LastNameModalProps) {
  const [lastName, setLastName] = useState("");

  if (!isOpen) return null;

  const isBlocked = Boolean(blockedUntil && blockedUntil > Date.now());

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (lastName.trim() && !isLoading && !isBlocked) {
      onSubmit(lastName.trim());
    }
  };

  // Libellé d'affichage du type d'évaluation
  const evaluationLabel = evaluationType
    ? EVALUATION_TYPE_LABELS[evaluationType] || evaluationType
    : "évaluation";

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="h-6 w-6 text-primary" />
          </div>
          <CardTitle>Vérification d'identité</CardTitle>
          <CardDescription>
            {isAnonymous
              ? `Pour accéder à votre évaluation ${evaluationLabel}, veuillez saisir votre code de reprise.`
              : `Pour accéder à votre évaluation ${evaluationLabel}, veuillez saisir votre nom de famille tel qu'il a été enregistré.`}
          </CardDescription>
        </CardHeader>

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="lastName">
                {isAnonymous ? "Code de reprise" : "Nom de famille"}
              </Label>
              <Input
                id="lastName"
                type="text"
                placeholder={isAnonymous ? "Ex : 028-EC0" : "Entrez votre nom de famille"}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={isLoading || isBlocked}
                autoFocus
                autoComplete={isAnonymous ? "off" : "family-name"}
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <p>{error}</p>
                  {isBlocked && blockedUntil && (
                    <p className="mt-1 text-xs opacity-80">
                      Réessayez dans {formatRemainingTime(blockedUntil)}
                    </p>
                  )}
                </div>
              </div>
            )}
          </CardContent>

          <CardFooter className="pt-4">
            <Button
              type="submit"
              className="w-full"
              disabled={!lastName.trim() || isLoading || isBlocked}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Vérification...
                </>
              ) : (
                "Accéder à l'évaluation"
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
